import React, { useEffect } from "react";
import { ArticleCard } from "./ArticleCard";
import Grid from "./Grid";
import { lowestPriceProduct } from "../utils/productHelpers";
import Container from "./Container";
import Section from "./Section";
import { useProductCategory } from "../context/ProductCategoryContext";
import { useProductCategories } from "../hooks/useProductCategories";
import styled, { css } from "styled-components";

const StyledGrid = styled.div`
  ${({ ...props }) => css`
    .grid-container {
      margin-left: -8px;
      margin-right: -8px;
    }

    .grid-item {
      padding-left: 8px;
      padding-right: 8px;
    }
  `}
`;

const getPrice = (categoryData, product, type) => {
  if (type !== 'buyPrice' && type !== 'hirePrice') return null;

  if (product.productData.pageType === 'category') {
    const category = categoryData.find((cat) => cat.slug === product.slug);

    if (category) {
      return lowestPriceProduct(category.products.nodes, type)?.productData?.productMeta?.[type];
    }
  }

  return product.productData.productMeta[type] ?? null
};

// Completely ugly hack because the `ReOrder Posts within Categories` WordPress plugin
//  does not actually store order, it just calculates it on the fly
//  and our order is correct on load, but not on reload
const productOrderCache = new Map();

function useEnrichedProducts(products, allCategories, sort = 'menu') {
  return React.useMemo(() => {
    if (!Array.isArray(products)) return [];

    const enrichedProducts = [...products]
      .filter(product => !product.productData.location)
      .map((product, index) => {
        let featuredImageData = null;
        let rolloverImageData = null;
  
        if (product.featuredImage) {
          featuredImageData = product.featuredImage.node.mediaItemUrl;
        } else if (
          product.productData.productGallery &&
          product.productData.productGallery.length > 0
        ) {
          featuredImageData = product.productData.productGallery[0].mediaItemUrl;
        }
  
        if (product.rolloverImage?.rolloverImage) {
          rolloverImageData = product.rolloverImage.rolloverImage.mediaItemUrl;
        }

        // Cache the order
        const cacheKey = product.id;
        if (!productOrderCache.has(cacheKey)) {
          productOrderCache.set(
            cacheKey,
            index
          );
        }
  
        return {
          ...product,
          featuredImageData,
          rolloverImageData,
          sortIndex: productOrderCache.get(cacheKey),
          sortPrice: getPrice(allCategories, product, 'buyPrice')?.price || 0,
        };
      })

    if (sort == 'price') {
      return enrichedProducts.sort((a, b) => {
        if (a.sortPrice !== b.sortPrice) {
          return a.sortPrice - b.sortPrice;
        }
  
        return a.title.localeCompare(b.title);
      });
    }

    return enrichedProducts.sort((a, b) => a.sortIndex - b.sortIndex);
  }, [products, allCategories, sort]);
}

export const ProductList = ({
  products,
  grey,
  addToContext = true,
  inner = false,
  className,
  sort = 'menu'
}) => {
  const { addProducts } = useProductCategory();
  const categoryData = useProductCategories()

  useEffect(() => {
    if (Array.isArray(products) && addToContext) {
      addProducts(products);
    }
  }, [products]);

  const enrichedProducts = useEnrichedProducts(products, categoryData, sort);

  const List = () => <StyledGrid>
    <Grid as="ul" equalHeight className="grid-container">
      {enrichedProducts.map((product) => {
        return (
          <Grid item as="li" xs="6" lg="4" xl="3" key={product.id} className="grid-item">
            <ArticleCard
              url={product.uri}
              title={product.title}
              featuredImage={product.featuredImageData}
              rolloverImage={product.rolloverImageData}
              catColour
              product="true"
              specification={product.productData?.specification}
              buyPrice={getPrice(categoryData, product, 'buyPrice')}
              hirePrice={getPrice(categoryData, product, 'hirePrice')}
            />
          </Grid>
        );
      })}
    </Grid>
  </StyledGrid>

  if (inner) {
    return <List className={className} />
  }

  return (
    <Section grey={grey} className={className}>
      <Container>
        <List />
      </Container>
    </Section>
  )
};
